import React, { createContext, useContext, useState } from 'react';
import eventEmitter, { EVENT_TYPES } from './ContextEvents';

const EmployeesContext = createContext();

export const useEmployees = () => {
  const context = useContext(EmployeesContext);
  if (!context) {
    throw new Error('useEmployees must be used within an EmployeesProvider');
  }
  return context;
};

export const EmployeesProvider = ({ children }) => {
  // Initialize employees from localStorage or use default values
  const [employees, setEmployees] = useState(() => {
    const savedEmployees = localStorage.getItem('facit_employees');
    return savedEmployees ? JSON.parse(savedEmployees) : [
      {
        id: 1,
        firstName: 'Ahmed',
        lastName: 'Belkacem',
        email: 'ahmed.belkacem@bridge-it.com',
        phone: '+213 21 456 789',
        address: '15 Rue Didouche Mourad',
        city: 'Alger',
        postalCode: '16000',
        position: 'Développeur Full Stack',
        department: 'Développement',
        salary: 80000,
        hireDate: '2023-03-15',
        status: 'active',
        employeeId: 'EMP001',
        manager: 'Direction',
        workType: 'full-time',
        skills: ['React', 'Node.js', 'JavaScript', 'Python']
      },
      {
        id: 2,
        firstName: 'Fatima',
        lastName: 'Benali',
        email: 'fatima.benali@bridge-it.com',
        phone: '+213 31 234 567',
        address: '45 Boulevard 1er Novembre',
        city: 'Oran',
        postalCode: '31000',
        position: 'Designer UX/UI',
        department: 'Design',
        salary: 65000,
        hireDate: '2023-06-20',
        status: 'active',
        employeeId: 'EMP002',
        manager: 'Ahmed Belkacem',
        workType: 'full-time',
        skills: ['Figma', 'Adobe Creative', 'UI Design', 'UX Research']
      },
      {
        id: 3,
        firstName: 'Karim',
        lastName: 'Messaoudi',
        email: 'karim.messaoudi@bridge-it.com',
        phone: '+213 25 345 678',
        address: '78 Rue Ben Badis',
        city: 'Constantine',
        postalCode: '25000',
        position: 'Chef de Projet',
        department: 'Management',
        salary: 95000,
        hireDate: '2023-01-10',
        status: 'active',
        employeeId: 'EMP003',
        manager: 'Direction',
        workType: 'full-time',
        skills: ['Gestion de projet', 'Scrum', 'Leadership', 'Communication']
      },
      {
        id: 4,
        firstName: 'Amina',
        lastName: 'Kaddour',
        email: 'amina.kaddour@bridge-it.com',
        phone: '+213 21 123 890',
        address: '2 Rue Didouche Mourad',
        city: 'Alger',
        postalCode: '16000',
        position: 'Développeur Backend',
        department: 'Développement',
        salary: 75000,
        hireDate: '2023-08-05',
        status: 'active',
        employeeId: 'EMP004',
        manager: 'Ahmed Belkacem',
        workType: 'full-time',
        skills: ['Java', 'Spring', 'PostgreSQL', 'Docker']
      },
      {
        id: 5,
        firstName: 'Omar',
        lastName: 'Brahimi',
        email: 'omar.brahimi@bridge-it.com',
        phone: '+213 21 567 890',
        address: '12 Avenue Pasteur',
        city: 'Alger',
        postalCode: '16030',
        position: 'Testeur QA',
        department: 'Qualité',
        salary: 55000,
        hireDate: '2023-09-12',
        status: 'inactive',
        employeeId: 'EMP005',
        manager: 'Karim Messaoudi',
        workType: 'part-time',
        skills: ['Tests automatisés', 'Selenium', 'Jest', 'Cypress']
      }
    ];
  });

  // Employee management methods
  const addEmployee = (employee) => {
    const newEmployee = {
      id: employees.length + 1,
      status: 'active',
      employeeId: `EMP${(employees.length + 1).toString().padStart(3, '0')}`,
      hireDate: employee.hireDate || new Date().toISOString().split('T')[0],
      ...employee
    };
    
    const updatedEmployees = [...employees, newEmployee];
    setEmployees(updatedEmployees);
    
    // Save to localStorage for persistence
    localStorage.setItem('facit_employees', JSON.stringify(updatedEmployees));
    
    // Emit events
    eventEmitter.emit(EVENT_TYPES.EMPLOYEE_CREATED, newEmployee);
    eventEmitter.emit(EVENT_TYPES.DATA_CHANGED, 'employees');
    
    return newEmployee;
  };

  const updateEmployee = (id, updatedEmployee) => {
    const oldEmployee = employees.find(employee => employee.id === id);
    const newEmployees = employees.map(employee => 
      employee.id === id ? { ...employee, ...updatedEmployee } : employee
    );
    
    setEmployees(newEmployees);
    
    // Save to localStorage for persistence
    localStorage.setItem('facit_employees', JSON.stringify(newEmployees));
    
    // Emit events
    eventEmitter.emit(EVENT_TYPES.EMPLOYEE_UPDATED, {
      oldEmployee,
      newEmployee: newEmployees.find(employee => employee.id === id)
    });
    eventEmitter.emit(EVENT_TYPES.DATA_CHANGED, 'employees');
  };

  const deleteEmployee = (id) => {
    const employeeToDelete = employees.find(employee => employee.id === id);
    const newEmployees = employees.filter(employee => employee.id !== id);
    setEmployees(newEmployees);
    
    // Save to localStorage for persistence
    localStorage.setItem('facit_employees', JSON.stringify(newEmployees));
    
    // Emit events
    eventEmitter.emit(EVENT_TYPES.EMPLOYEE_DELETED, employeeToDelete);
    eventEmitter.emit(EVENT_TYPES.DATA_CHANGED, 'employees');
  };

  const getEmployeeById = (id) => {
    return employees.find(employee => employee.id === id);
  };

  const getActiveEmployees = () => {
    return employees.filter(employee => employee.status === 'active');
  };

  const getDepartments = () => {
    return [...new Set(employees.map(e => e.department))].filter(Boolean);
  };

  const getPositions = () => {
    return [...new Set(employees.map(e => e.position))].filter(Boolean);
  };

  const getManagerOptions = () => {
    return employees
      .filter(e => e.status === 'active' && (e.position.toLowerCase().includes('manager') || 
                                            e.position.toLowerCase().includes('chef') || 
                                            e.position.toLowerCase().includes('directeur') ||
                                            e.position === 'Direction'))
      .map(e => `${e.firstName} ${e.lastName}`);
  };

  const getTotalSalaries = () => {
    return employees
      .filter(e => e.status === 'active')
      .reduce((sum, e) => sum + (Number(e.salary) || 0), 0);
  };

  return (
    <EmployeesContext.Provider value={{
      employees,
      addEmployee,
      updateEmployee,
      deleteEmployee,
      getEmployeeById,
      getActiveEmployees,
      getDepartments,
      getPositions,
      getManagerOptions,
      getTotalSalaries
    }}>
      {children}
    </EmployeesContext.Provider>
  );
}; 